/**
 * Authentication Context
 * Manages user session and authentication state
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from './api-client';
import type { User } from '@/types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  canAccessOrigin: (formOrigin: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      const currentUser = await apiClient.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }

  function login() {
    // Redirect to Entra ID OAuth login
    window.location.href = '/api/admin/auth/login';
  }

  async function logout() {
    try {
      await apiClient.logout();
      setUser(null);
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }

  function hasPermission(permission: string): boolean {
    if (!user) return false;

    const rolePermissions: Record<string, string[]> = {
      super_admin: ['*'], // All permissions
      administrator: [
        'submissions:read', 'submissions:write', 'submissions:delete',
        'files:read', 'files:write', 'files:delete',
        'security:read', 'security:write',
        'api_keys:read', 'api_keys:write',
        'config:read',
      ],
      content_manager: [
        'submissions:read', 'submissions:write',
        'files:read',
        'security:read',
      ],
      viewer: [
        'submissions:read',
        'files:read',
        'security:read',
      ],
      auditor: [
        'submissions:read',
        'files:read',
        'security:read',
      ],
    };

    const userPermissions = rolePermissions[user.role] || [];
    return userPermissions.includes('*') || userPermissions.includes(permission);
  }

  function canAccessOrigin(formOrigin: string): boolean {
    if (!user) return false;

    // super_admin and administrator can access all
    if (user.role === 'super_admin' || user.role === 'administrator') {
      return true;
    }

    // content_manager has specific form access
    if (user.role === 'content_manager' && user.form_access) {
      return user.form_access.includes(formOrigin);
    }

    // viewer and auditor can access all (read-only)
    return user.role === 'viewer' || user.role === 'auditor';
  }

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    hasPermission,
    canAccessOrigin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
